import { IconButton } from '@material-ui/core';
import {
  Delete as DeleteIcon, Search as PageviewIcon,
} from '@material-ui/icons';
import React, {
  useCallback, useContext, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import ContaBancariaContext from '../../../../../contexts/registrations/conta-bancaria/ContaBancariaContext';
import { resetList as resetBancoList } from '../../../../../features/banco/BancoSlice';
import { resetList as resetMoedaList } from '../../../../../features/moeda/moedaSlice';
import {
  activateByIdAsync,
  fetchAllAsync,
  inactivateByIdAsync,
  resetMode,
  resetModel,
  selectContasBancarias,
} from '../../../../../features/conta-bancaria/contaBancariaSlice';
import QCXSimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/QCXSimpleConsultPageTemplate';
import { formatContaCorrente } from '../../../../../utils/hooks/form/field/formatters';
import useOperationController from '../../../../../utils/hooks/store/operation/useOperationController';
import { isAlternativeLoadingStatus } from '../../../../../utils/store/store-utils';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';

export default function ContaBancariaConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();

  const history = useHistory();

  const {
    status,
  } = useContext(ContaBancariaContext);

  const dispatch = useDispatch();

  const contasBancarias = useSelector(selectContasBancarias);

  const isAlternativeLoading = useMemo(() => (
    isAlternativeLoadingStatus(status)
  ), [status]);

  const fetchAll = useCallback(() => {
    dispatch(fetchAllAsync());
  }, []);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
  }, []);

  const resetDependencies = useCallback(() => {
    dispatch(resetBancoList());
    dispatch(resetMoedaList());
  }, []);

  useEffect(() => {
    fetchAll();
    resetModes();
    resetModels();
    resetDependencies();
  }, []);

  const handleActivateInactivate = useOperationController(
    async (event, id) => {
      event.preventDefault();

      const { active } = contasBancarias.find(
        (contaBancaria) => (
          contaBancaria?.id === id
        )
      );

      const isActivation = !active;

      if (isActivation) {
        dispatch(activateByIdAsync(id));
      } else {
        dispatch(inactivateByIdAsync(id));
      }
    }
  );

  const handleClickOnEditButton = useCallback((event, id) => {
    event.stopPropagation();
    history.push(t('com.muralis.qcx.url.modulosFinanceirosContasBancariasDetalhes', { id }));
  }, [history]);

  const columns = [
    {
      field: 'id',
      headerName: t('com.muralis.qcx.codigo'),
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      flex: 50,
      valueGetter: ({ row }) => (
        row?.id
      ),
    },
    {
      field: 'description',
      headerName: t('com.muralis.qcx.descricao'),
      type: 'string',
      flex: 100,
      valueGetter: ({ row }) => (
        row?.description
      ),
    },
    {
      field: 'banco',
      headerName: t('com.muralis.qcx.banco.label'),
      type: 'string',
      flex: 100,
      valueGetter: ({ row }) => (
        row?.banco?.description
      ),
    },
    {
      field: 'agencia',
      headerName: t('com.muralis.qcx.banco.agencia'),
      type: 'string',
      flex: 50,
      valueGetter: ({ row }) => (
        row?.agencia || '-'
      ),
    },
    {
      field: 'contaCorrente',
      headerName: t('com.muralis.qcx.banco.contaCorrente'),
      type: 'string',
      flex: 70,
      valueGetter: ({ row }) => (
        formatContaCorrente(row?.contaCorrente) || '-'
      ),
    },
    {
      field: 'actions',
      headerName: t('com.muralis.qcx.acoes.label'),
      headerAlign: 'center',
      align: 'center',
      flex: 80,
      renderCell: ({ row: { id, active } }) => (
        <>
          {userHasDeleteRole(authInfo, 'conta-bancaria') && (
          <IconButton
            key={`stw-active${id}`}
            value={active}
            name={`stw-active-${id}`}
            onClick={(event) => handleActivateInactivate(event, id)}
          >
            <DeleteIcon color="error" />
          </IconButton>
          )}
          <IconButton
            key={`btn-view-${id}`}
            name={`btn-view-${id}`}
            onClick={(event) => handleClickOnEditButton(event, id)}
          >
            <PageviewIcon color="primary" />
          </IconButton>
        </>
      ),
    },
  ];

  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.cadastros'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloCadastrosFinanceiros'),
        name: t('com.muralis.qcx.financeiro.labelPlural'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.contaBancaria.labelPlural'),
      },
    },
  ];

  const templateProps = {
    page: {
      title: t('com.muralis.qcx.contaBancaria.labelPlural'),
      breadcrumbs,
    },
    control: {
      buttonNew: {
        description: t('com.muralis.qcx.contaBancaria.novaContaBancaria'),
        path: t('com.muralis.qcx.url.modulosFinanceirosContasBancariasNova'),
      },
    },
    table: {
      columns,
    },
  };

  return (
    <QCXSimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={contasBancarias}
      isLoading={isAlternativeLoading}
      buttonNewText={t('com.muralis.qcx.acoes.nova').toUpperCase()}
      multipleFilter
      authInfo={authInfo}
      requiredRoles={['conta-bancaria']}
    />
  );
}
